import * as signalR from "@microsoft/signalr";
import CloseIcon from "@mui/icons-material/Close";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import ReplyIcon from "@mui/icons-material/Reply";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SendIcon from "@mui/icons-material/Send";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import {
  Link as RouterLink,
  useParams,
  useRouteLoaderData,
} from "react-router-dom";
import { isAxiosError } from "axios";
import { toast } from "react-toastify";
import usePost from "../../app/usePost";
import { axiosApi } from "../../interceptors/axios";
import { PagedResponse, Result } from "../../types/Result";
import { AiReplySuggestion, SystemChat } from "./type";
import { User } from "../user/type";

export default function SystemChatForm() {
  const { data, connection, user } = useRouteLoaderData(
    "systemChatRoute"
  ) as {
    data: PagedResponse<SystemChat>;
    connection: signalR.HubConnection;
    user: User | null;
  };
  const { userId } = useParams();

  // State değişkenleri
  const [messages, setMessages] = useState<SystemChat[]>(data?.data || []);
  const [editingMessage, setEditingMessage] = useState<SystemChat | null>(null);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(data?.hasNext || false);
  const [currentPage, setCurrentPage] = useState(data?.currentPage || 1);
  const [replyTo, setReplyTo] = useState<SystemChat | null>(null);
  const [messageText, setMessageText] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { post: postSuggest, waiting: aiLoading } =
    usePost<AiReplySuggestion>();

  // SignalR bağlantısını kur
  useEffect(() => {
    connection.on("ReceiveSystemMessage", (newMessage: SystemChat) => {
      setMessages((prev) => [newMessage, ...prev]);
    });
    return () => {
      connection.off("ReceiveSystemMessage");
    };
  }, [userId]);

  // Daha fazla mesaj yükle
  const loadMoreMessages = async () => {
    if (!hasMore || loading) return;

    try {
      setLoading(true);
      const response = await axiosApi.get<PagedResponse<SystemChat>>(
        `/api/SystemChats?pageNumber=${
          currentPage + 1
        }&pageSize=10&filters[0].field=userId&filters[0].operator=eq&filters[0].value=${userId}&sort.field=creationTime&sort.sort=desc`
      );

      if (response.data.isSuccess && response.data.data) {
        setMessages((prev) => [...prev, ...response.data.data!]);
        setCurrentPage(response.data.currentPage + 1);
        setHasMore(response.data.hasNext);
      }
    } catch (error) {
      console.error("Mesajlar yüklenirken hata:", error);
    } finally {
      setLoading(false);
    }
  };
  const sendMessage = async () => {
    if (!messageText.trim()) return;

    // Eğer düzenleme modundaysak, düzenlenmiş mesajı kaydet
    if (editingMessage) {
      await saveEditedMessage();
      return;
    }

    try {
      setLoading(true);
      const payload = {
        message: messageText,
        refChatId: replyTo?.id || null,
        isSystem: true,
        userId: userId,
        fullName: "Destek Ekibi",
      };

      // API ile mesajı gönder
      const response = await axiosApi.post<Result<SystemChat>>(
        "/api/SystemChats",
        payload
      );

      if (response.data.isSuccess) {
        setMessageText("");
        setReplyTo(null);
        connection.invoke("SendMessageByUserId", userId, response.data.data);
      }
    } catch (error) {
      console.error("Mesaj gönderilirken hata:", error);
    } finally {
      setLoading(false);
    }
  };
  const deleteMessage = async (messageId: string) => {
    var isConfirm = window.confirm("Silmek istedeğinize emin misiniz");
    if (!isConfirm) return;
    try {
      setLoading(true);
      const payload = {
        message: messageText,
        refChatId: replyTo?.id || null,
        isSystem: true,
        userId: userId,
        fullName: "Destek Ekibi",
      };

      // API ile mesajı gönder
      const response = await axiosApi.delete<Result<void>>(
        `/api/SystemChats/${messageId}`
      );
      if (response.data.isSuccess) {
        setMessages((prevMessages) =>
          prevMessages.filter((d) => d.id !== messageId)
        );
      }
    } catch (error) {
      console.error("Mesaj silinirken hata:", error);
    } finally {
      setLoading(false);
    }
  };
  // Mesajı okundu olarak işaretle
  const markAsRead = async (messageId: string) => {
    try {
      const response = await axiosApi.put<Result<boolean>>(
        `/api/SystemChats/${messageId}/read`
      );

      if (response.data.isSuccess) {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === messageId ? { ...msg, isRead: true } : msg
          )
        );
      }
    } catch (error) {
      console.error("Mesaj okundu olarak işaretlenirken hata:", error);
    }
  };

  // Tarih formatı
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  // Mesaj grupları oluştur (gün bazında)
  const groupMessagesByDate = () => {
    const groups: Record<string, SystemChat[]> = {};

    messages
      .slice()
      .reverse()
      .forEach((message) => {
        const date = new Date(message.creationTime);
        const dateKey = date.toISOString().split("T")[0];

        if (!groups[dateKey]) {
          groups[dateKey] = [];
        }

        groups[dateKey].push(message);
      });

    return Object.entries(groups)
      .map(([date, messages]) => {
        return {
          date,
          messages: messages.sort(
            (a, b) =>
              new Date(b.creationTime).getTime() -
              new Date(a.creationTime).getTime()
          ),
        };
      })
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  };

  // Mesajı yanıtla
  const handleReply = (message: SystemChat) => {
    setReplyTo(message);
    // Mesaj input'una odaklan
    document.getElementById("message-input")?.focus();
  };

  const handleEdit = (message: SystemChat) => {
    // Sadece sistem mesajları düzenlenebilir
    if (!message.isSystem) {
      return;
    }

    setEditingMessage(message);
    setMessageText(message.message);
    setReplyTo(null); // Düzenleme sırasında yanıtlama modunu kapat

    // Mesaj input'una odaklan
    document.getElementById("message-input")?.focus();
  };

  // Düzenlemeyi iptal et
  const cancelEdit = () => {
    setEditingMessage(null);
    setMessageText("");
  };

  // Düzenlenmiş mesajı kaydet
  const saveEditedMessage = async () => {
    if (!editingMessage || !messageText.trim()) return;

    try {
      setLoading(true);
      const payload = {
        id: editingMessage.id,
        message: messageText,
        refChatId: editingMessage.refChatId,
        isSystem: true,
        userId: userId,
        fullName: "Destek Ekibi",
        creationTime: editingMessage.creationTime,
        creatorId: editingMessage.creatorId,
      };

      // API ile mesajı güncelle
      const response = await axiosApi.put<Result<SystemChat>>(
        `/api/SystemChats`,
        payload
      );

      if (response.data.isSuccess) {
        // Local state'i güncelle
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === editingMessage.id
              ? { ...msg, message: messageText, isEdited: true }
              : msg
          )
        );

        setMessageText("");
        setEditingMessage(null);

        // SignalR ile diğer kullanıcılara güncellemeyi bildir
        // connection.invoke("UpdateMessageByUserId", userId, response.data.data);
      }
    } catch (error) {
      console.error("Mesaj güncellenirken hata:", error);
    } finally {
      setLoading(false);
    }
  };

  // AI ile cevap önerisi al (gönderilmez, sadece metin kutusunu doldurur)
  const suggestReply = async () => {
    if (editingMessage || aiLoading) return;

    if (messageText.trim()) {
      const isConfirm = window.confirm(
        "Mevcut metin silinecek, devam edilsin mi?"
      );
      if (!isConfirm) return;
    }

    try {
      const response = await postSuggest(
        axiosApi.post<Result<AiReplySuggestion>>(
          "/api/SystemChats/ai-suggest",
          {
            userId,
            targetChatId: replyTo?.id ?? null,
          }
        )
      );

      if (response.data.isSuccess && response.data.data) {
        setMessageText(response.data.data.suggestion);
        toast.success("AI önerisi eklendi. Göndermeden önce kontrol edin.");
        document.getElementById("message-input")?.focus();
      }
    } catch (error) {
      // 400 hataları usePost tarafından zaten toast ile gösterildi
      const alreadyToasted =
        isAxiosError(error) &&
        Array.isArray(error.response?.data?.errors) &&
        error.response?.data?.errors.length > 0;
      if (!alreadyToasted) {
        toast.error("AI önerisi alınamadı.");
      }
      console.error("AI önerisi alınırken hata:", error);
    }
  };

  // Yanıtlamayı iptal et
  const cancelReply = () => {
    setReplyTo(null);
  };

  // Klavye kısayolları
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const messageGroups = groupMessagesByDate();

  return (
    <Box sx={{ width: "100%", p: 2 }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h4" component="h1">
            Çözüm Merkezi
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {user
              ? `${user.fullName || user.userName}${
                  user.email ? ` · ${user.email}` : ""
                }`
              : userId}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            component={RouterLink}
            to={`/users/${userId}`}
            variant="outlined"
            startIcon={<PersonOutlineIcon />}
          >
            Kullanıcı Detayı
          </Button>
          <Button
            component={RouterLink}
            to={`/users/${userId}/chats`}
            variant="outlined"
            startIcon={<ForumOutlinedIcon />}
          >
            Yazışmaları
          </Button>
        </Box>
      </Box>

      <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
        <Box sx={{ mb: 2 }}>
          {replyTo && (
            <Paper
              elevation={1}
              sx={{
                p: 1,
                mb: 1,
                backgroundColor: "#f0f7ff",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <ReplyIcon sx={{ mr: 1, color: "primary.main" }} />
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary" }}
                  >
                    Yanıtlanıyor: {replyTo.fullName || "Kullanıcı"}
                  </Typography>
                  <Typography variant="body2" noWrap sx={{ maxWidth: 500 }}>
                    {replyTo.message}
                  </Typography>
                </Box>
              </Box>
              <IconButton size="small" onClick={cancelReply}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Paper>
          )}

          <TextField
            id="message-input"
            label={editingMessage ? "Mesajı Düzenle" : "Mesaj"}
            multiline
            rows={3}
            fullWidth
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder={
              editingMessage
                ? "Mesajınızı düzenleyin..."
                : "Sistem mesajınızı buraya yazın..."
            }
            sx={{ mb: 1 }}
          />

          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="contained"
                endIcon={<SendIcon />}
                onClick={sendMessage}
                disabled={loading || aiLoading || !messageText.trim()}
                color={editingMessage ? "warning" : "success"}
              >
                {editingMessage ? "Güncelle" : "Gönder"}
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={suggestReply}
                startIcon={
                  aiLoading ? (
                    <CircularProgress size={16} />
                  ) : (
                    <AutoAwesomeIcon />
                  )
                }
                disabled={
                  aiLoading ||
                  loading ||
                  !!editingMessage ||
                  messages.length === 0
                }
              >
                {aiLoading ? "Öneri hazırlanıyor…" : "AI Cevap Öner"}
              </Button>
            </Box>
            {editingMessage && (
              <Button
                variant="outlined"
                onClick={cancelEdit}
                sx={{ ml: 1 }}
                disabled={loading}
              >
                İptal
              </Button>
            )}
          </Box>
        </Box>
        {editingMessage && (
          <Paper
            elevation={1}
            sx={{
              p: 1,
              mb: 1,
              backgroundColor: "#fff3e0",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <EditIcon sx={{ mr: 1, color: "warning.main" }} />
              <Box>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Düzenleniyor
                </Typography>
                <Typography variant="body2" noWrap sx={{ maxWidth: 500 }}>
                  {editingMessage.message}
                </Typography>
              </Box>
            </Box>
            <IconButton size="small" onClick={cancelEdit}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Paper>
        )}
        {messages.length === 0 ? (
          <Typography
            variant="body1"
            sx={{ textAlign: "center", py: 4, color: "text.secondary" }}
          >
            Henüz mesaj bulunmamaktadır.
          </Typography>
        ) : (
          <Box sx={{ maxHeight: 600, overflow: "auto" }}>
            {messageGroups.map((group, groupIndex) => (
              <Box key={group.date} sx={{ mb: 3 }}>
                <Divider sx={{ my: 2 }}>
                  <Chip
                    label={new Date(group.date).toLocaleDateString("tr-TR", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}
                  />
                </Divider>

                {group.messages.map((message, messageIndex) => (
                  <Card
                    key={message.id}
                    sx={{
                      mb: 1.5,
                      backgroundColor: message.isSystem
                        ? "#f0f7ff"
                        : !message.isRead && !message.isSystem
                        ? "#fffde7"
                        : "#fff",
                      borderLeft: message.isSystem
                        ? "4px solid #1976d2"
                        : !message.isRead && !message.isSystem
                        ? "4px solid #ffc107"
                        : "none",
                    }}
                  >
                    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                      <Grid container spacing={1}>
                        <Grid size={{ xs: 12 }}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              mb: 1,
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <Typography variant="subtitle2" color="primary">
                                {message.isSystem
                                  ? "Sistem"
                                  : message.fullName || "Kullanıcı"}
                              </Typography>
                              {message.isSystem && (
                                <Chip
                                  label="Sistem Mesajı"
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                  sx={{ ml: 1, height: 20 }}
                                />
                              )}
                              {!message.isSystem && !message.isRead && (
                                <Chip
                                  label="Okunmadı"
                                  size="small"
                                  color="warning"
                                  variant="outlined"
                                  sx={{ ml: 1, height: 20 }}
                                />
                              )}
                              {!message.isSystem && message.isRead && (
                                <Tooltip title="Okundu">
                                  <DoneAllIcon
                                    color="primary"
                                    fontSize="small"
                                    sx={{ ml: 1 }}
                                  />
                                </Tooltip>
                              )}
                            </Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {formatDate(message.creationTime)}
                            </Typography>
                          </Box>
                        </Grid>

                        {/* Yanıtlanan mesaj varsa göster */}
                        {message.refChat && (
                          <Grid size={{ xs: 12 }}>
                            <Paper
                              elevation={0}
                              sx={{
                                p: 1,
                                mb: 1,
                                backgroundColor: "#f5f5f5",
                                borderLeft: "3px solid #9e9e9e",
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Yanıtlanan:{" "}
                                {message.refChat.fullName || "Kullanıcı"}
                              </Typography>
                              <Typography variant="body2">
                                {message.refChat.message}
                              </Typography>
                            </Paper>
                          </Grid>
                        )}

                        <Grid size={{ xs: 12 }}>
                          <Typography
                            variant="body1"
                            sx={{ whiteSpace: "pre-wrap" }}
                          >
                            {message.message}
                          </Typography>
                        </Grid>

                        <Grid
                          size={{ xs: 12 }}
                          sx={{ display: "flex", justifyContent: "flex-end" }}
                        >
                          <Button
                            size="small"
                            startIcon={<DeleteIcon />}
                            onClick={() => deleteMessage(message.id)}
                          >
                            Sil
                          </Button>
                          <Button
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={() => handleEdit(message)}
                            disabled={!message.isSystem} // Sadece sistem mesajları düzenlenebilir
                            sx={{
                              opacity: message.isSystem ? 1 : 0.5,
                              cursor: message.isSystem
                                ? "pointer"
                                : "not-allowed",
                            }}
                          >
                            Düzenle
                          </Button>
                          <Button
                            size="small"
                            startIcon={<ReplyIcon />}
                            onClick={() => handleReply(message)}
                          >
                            Yanıtla
                          </Button>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            ))}

            {/* Daha fazla mesaj yükleme */}
            {hasMore && (
              <Box sx={{ textAlign: "center", mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={loadMoreMessages}
                  disabled={loading}
                >
                  {loading ? (
                    <CircularProgress size={24} />
                  ) : (
                    "Daha fazla mesaj yükle"
                  )}
                </Button>
              </Box>
            )}

            <div ref={messagesEndRef} />
          </Box>
        )}
      </Paper>
    </Box>
  );
}
